import { createContext, useContext, useEffect, useState } from "react";

import { authApi } from "@/features/auth/api/auth-api";
import type { AuthUser, LoginInput } from "@/features/auth/types";
import {
  getRefreshToken,
  getStoredUser,
  setSession,
  updateStoredUser,
} from "@/lib/auth-storage";

interface AuthContextValue {
  user: AuthUser | null;
  /** True while the app is trying to restore a session on boot. */
  isLoading: boolean;
  login: (input: LoginInput) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Render immediately from whatever's persisted so there's no boot flash, then refresh
    // in the background via /auth/me — this is what picks up permission/field-visibility/
    // column-order changes made server-side without forcing a logout/login cycle. If the
    // stored token has actually expired, this 401s and the axios interceptor's `auth:logout`
    // handler (below) clears the session same as any other API call would.
    const storedUser = getStoredUser<AuthUser>();
    setUser(storedUser);
    setIsLoading(false);

    if (storedUser) {
      authApi
        .me()
        .then((freshUser) => {
          updateStoredUser(freshUser);
          setUser(freshUser);
        })
        .catch(() => undefined);
    }
  }, []);

  useEffect(() => {
    const onForceLogout = () => setUser(null);
    window.addEventListener("auth:logout", onForceLogout);
    return () => window.removeEventListener("auth:logout", onForceLogout);
  }, []);

  async function login(input: LoginInput) {
    const { accessToken, refreshToken, user: loggedInUser } = await authApi.login(input);
    setSession({ accessToken, refreshToken, user: loggedInUser });
    setUser(loggedInUser);
  }

  async function logout() {
    const refreshToken = getRefreshToken();
    if (refreshToken) {
      await authApi.logout(refreshToken).catch(() => undefined);
    }
    setSession(null);
    setUser(null);
  }

  return (
    <AuthContext.Provider value={{ user, isLoading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within an AuthProvider");
  return context;
}
